/**
 * BannerSafeZonePreview — shows a generated banner with YouTube's
 * three crop zones overlaid, exactly like YT Studio's "Customize
 * banner art" dialog.
 *
 *   TV view        : full 2560×1440 (everyone with a TV / smart-TV browser
 *                    sees the entire image)
 *   Desktop band   : 2560×423 horizontal strip, centered vertically
 *   Mobile-safe    : 1546×423 — the most restrictive crop; this is what
 *                    a phone user sees by default
 *
 * Click a tab to switch which crop is highlighted. The "Mobile" tab is
 * the most useful — if the hero subject isn't inside that box, the
 * banner is going to look broken on most viewers.
 *
 * We render at a fixed 16:9 aspect ratio via padding-bottom so the
 * percentages of each crop box land on the same px regardless of how
 * wide the parent column is.
 */
import { useState } from "react";

const ZONES = [
  {
    key: "center",
    label: "Center-safe",
    sub: "1235×338 · text + faces MUST fit here",
    // The universally-safe box — visible on every device (per YouTube
    // spec). All text, logos, and faces must live inside this box.
    // On a 2560×1440 canvas: x=(2560-1235)/2=662.5, y=(1440-338)/2=551
    leftPct: ((2560 - 1235) / 2 / 2560) * 100,
    topPct: ((1440 - 338) / 2 / 1440) * 100,
    widthPct: (1235 / 2560) * 100,
    heightPct: (338 / 1440) * 100,
    ring: "ring-red-500",
    label_bg: "bg-red-500",
    label_text: "text-white",
  },
  {
    key: "mobile",
    label: "Mobile",
    sub: "1546×423 · most viewers",
    // Center-mounted, narrow & short. Coordinates are % of the 16:9 frame.
    // On a 2560×1440 canvas: x=(2560-1546)/2=507, y=(1440-423)/2=508.5
    leftPct: ((2560 - 1546) / 2 / 2560) * 100,
    topPct: ((1440 - 423) / 2 / 1440) * 100,
    widthPct: (1546 / 2560) * 100,
    heightPct: (423 / 1440) * 100,
    ring: "ring-emerald-400",
    label_bg: "bg-emerald-400",
    label_text: "text-black",
  },
  {
    key: "desktop",
    label: "Desktop",
    sub: "2560×423 wide band",
    leftPct: 0,
    topPct: ((1440 - 423) / 2 / 1440) * 100,
    widthPct: 100,
    heightPct: (423 / 1440) * 100,
    ring: "ring-yellow-400",
    label_bg: "bg-yellow-400",
    label_text: "text-black",
  },
  {
    key: "tv",
    label: "TV",
    sub: "Full 2560×1440",
    leftPct: 0,
    topPct: 0,
    widthPct: 100,
    heightPct: 100,
    ring: "ring-zinc-400",
    label_bg: "bg-zinc-300",
    label_text: "text-black",
  },
];

export function BannerSafeZonePreview({ src, alt = "Banner preview", onError }) {
  // Default to "tv" — show the FULL 2560×1440 banner as-is with no
  // dimmer overlay, because that's what users actually want to look
  // at when reviewing the generated art. Previously defaulted to
  // "desktop" which dimmed the top and bottom bands: users mistook
  // the dimmed regions for "the AI clipped my artwork" when the full
  // image was actually there all along. Desktop / Mobile safe-zone
  // views are still one click away for anyone who wants to verify
  // where the crops land.
  const [active, setActive] = useState("tv");
  const activeZone = ZONES.find((z) => z.key === active);

  return (
    <div data-testid="banner-safezone-preview" className="space-y-2">
      <div className="relative w-full overflow-hidden rounded-sm bg-black border border-zinc-800">
        {/* 16:9 box. padding-bottom trick keeps aspect ratio without JS. */}
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <img
            src={src}
            alt={alt}
            onError={onError}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="banner-safezone-preview-img"
          />
          {/* Dimmer overlay outside the active safe zone — gives the
              "this part gets cropped" effect. We draw 4 dimmer rects
              around the safe zone instead of a clip-path (better
              browser support and dead-simple math). */}
          {active !== "tv" && activeZone ? (
            <>
              {/* top dimmer */}
              <div
                className="absolute left-0 top-0 right-0 bg-black/55 pointer-events-none"
                style={{ height: `${activeZone.topPct}%` }}
              />
              {/* bottom dimmer */}
              <div
                className="absolute left-0 right-0 bottom-0 bg-black/55 pointer-events-none"
                style={{ height: `${100 - activeZone.topPct - activeZone.heightPct}%` }}
              />
              {/* left dimmer */}
              <div
                className="absolute left-0 bg-black/55 pointer-events-none"
                style={{
                  top: `${activeZone.topPct}%`,
                  height: `${activeZone.heightPct}%`,
                  width: `${activeZone.leftPct}%`,
                }}
              />
              {/* right dimmer */}
              <div
                className="absolute right-0 bg-black/55 pointer-events-none"
                style={{
                  top: `${activeZone.topPct}%`,
                  height: `${activeZone.heightPct}%`,
                  width: `${100 - activeZone.leftPct - activeZone.widthPct}%`,
                }}
              />
            </>
          ) : null}
          {/* Crisp outline of the active zone — matches YT Studio's
              "Viewable on …" bracket markers. */}
          {activeZone ? (
            <div
              data-testid={`banner-safezone-${active}-outline`}
              className={`absolute pointer-events-none ring-2 ${activeZone.ring}`}
              style={{
                left: `${activeZone.leftPct}%`,
                top: `${activeZone.topPct}%`,
                width: `${activeZone.widthPct}%`,
                height: `${activeZone.heightPct}%`,
              }}
            >
              <span
                className={`absolute -top-0 left-2 font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-[2px] leading-none ${activeZone.label_bg} ${activeZone.label_text}`}
              >
                {activeZone.label} ▸ {activeZone.sub}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mr-1">
          Preview ▸
        </span>
        {ZONES.map((z) => (
          <button
            key={z.key}
            type="button"
            data-testid={`banner-safezone-tab-${z.key}`}
            onClick={() => setActive(z.key)}
            title={`${z.label} · ${z.sub}`}
            className={
              "font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border transition-colors " +
              (active === z.key
                ? "border-yellow-400 text-yellow-400 bg-yellow-400/10"
                : "border-zinc-800 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400")
            }
          >
            {z.label}
          </button>
        ))}
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600 ml-auto normal-case tracking-normal">
          {activeZone?.sub}
        </span>
      </div>
    </div>
  );
}
